import { LEGACY_CUSTOMER, pickLanguage } from './i18n/customer'
import type { LayoutDocument } from '../layout/types'
import { aheadFromPosition, serviceName, type Language } from './kioskText'
import type { PrintableTicket } from './printer'
import { slipWait, type Estimate } from './estimate'
import type { Service, Ticket } from './types'

export interface SlipInput {
  ticket: Pick<Ticket, 'displayNumber' | 'issuedAt' | 'language'>
  position: number | undefined
  /** The server's estimate; absent or with no desk prints no wait. */
  estimate: Estimate | undefined
  service: Pick<Service, 'name' | 'code'> | undefined
  branch: { name: string; timezone: string } | undefined
  design: LayoutDocument | null | undefined
  pictures: Record<string, string>
  /** The link the slip's code carries, minted just before printing. */
  followUrl?: string
  /** The validity the follow link answered with; absent prints none. */
  validUntil?: string
  /** The organisation's customer languages; the first is the slip's default. */
  languages?: readonly Language[]
}

/**
 * The slip reception prints: the kiosk's slip, from what the reception desk knows.
 *
 * Three things are deliberately missing. Printer settings, because reception is
 * a person at a PC and not a unit — the agent's own flags decide the paper there.
 * Visit fields, because the issue route returns nothing filtered for paper, and a
 * ticket's own fields are unfiltered: a design's placeholder prints empty rather
 * than a personal value. And a language chosen by the customer, because reception
 * asks for none yet; the ticket's own is used when it has one.
 */
export function slipLanguage(lang: string | undefined, offered: readonly Language[] = LEGACY_CUSTOMER): Language {
  return pickLanguage(lang, offered)
}

export function receptionSlip(input: SlipInput): PrintableTicket {
  const language = slipLanguage(input.ticket.language, input.languages)
  const ahead = aheadFromPosition(input.position)
  return {
    number: input.ticket.displayNumber,
    branch: input.branch?.name,
    service: input.service ? serviceName(input.service.name, language, input.service.code, input.languages?.[0]) : undefined,
    ...(ahead !== undefined && { ahead, aheadKnown: true }),
    ...slipWait(input.estimate),
    issuedAt: input.ticket.issuedAt,
    timezone: input.branch?.timezone,
    language,
    ...(input.languages?.[0] && { defaultLanguage: input.languages[0] }),
    design: input.design ?? undefined,
    pictures: Object.keys(input.pictures).length > 0 ? input.pictures : undefined,
    fields: {},
    ...(input.followUrl && { followUrl: input.followUrl }),
    ...(input.validUntil && { validUntil: input.validUntil }),
  }
}
